import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/firestore';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import Header from '../Components/Header';
import ProductModal from '../Components/ProductModal';

const AlphaDogs = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [catalogItems, setCatalogItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const videoRefs = useRef({});

  // Загрузка категорий из Firebase
  useEffect(() => {
    fetchCategories();
  }, []);

  // Загрузка товаров из Firebase
  useEffect(() => {
    fetchCatalogItems();
  }, []);

  // Остановка видео при закрытии модального окна
  useEffect(() => {
    if (!selectedItem && playingVideo) {
      const videoElement = videoRefs.current[playingVideo];
      if (videoElement) {
        videoElement.pause();
        videoElement.currentTime = 0;
      }
      setPlayingVideo(null);
    }
  }, [selectedItem, playingVideo]);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const categoriesSnapshot = await getDocs(
        query(
          collection(db, "categories"),
          where("isActive", "==", true),
          orderBy("order")
        )
      );
      
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Добавляем категорию "Все" в начало
      const allCategories = [
        { id: 'all', name: 'Каталог тканей' },
        ...categoriesData
      ];
      
      setCategories(allCategories);
    } catch (err) {
      console.error('Ошибка при загрузке категорий:', err);
      // Если не удалось загрузить категории, используем дефолтные
      setCategories([
        { id: 'all', name: 'Все' }
      ]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchCatalogItems = async () => {
    try {
      setLoading(true);
      const catalogSnapshot = await getDocs(collection(db, "catalogItems"));
      const itemsData = catalogSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Сортируем по дате создания (новые первыми)
      itemsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB - dateA;
      });
      
      setCatalogItems(itemsData);
      setError(null);
    } catch (err) {
      console.error('Ошибка при загрузке каталога:', err);
      setError('Не удалось загрузить каталог. Пожалуйста, проверьте подключение и попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  // Получаем название категории по ID
  const getCategoryName = (categoryId) => {
    if (categoryId === 'all') return 'Все';
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Каталог тканей';
  };

  // Фильтруем товары по выбранной категории
  const filteredItems = catalogItems.filter(item => {
    if (selectedCategory !== 'all' && item.category !== selectedCategory) {
      return false;
    }
    return true;
  });

  // Получаем количество товаров в каждой категории
  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') return catalogItems.length;
    return catalogItems.filter(item => item.category === categoryId).length;
  };

  // Функция для определения типа видео
  const getVideoType = (url) => {
    if (!url) return null;
    
    if (url.includes('rutube.ru') || url.includes('rutube')) {
      return 'rutube';
    } else if (url.includes('vimeo.com')) {
      return 'vimeo';
    } else if (url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov')) {
      return 'direct';
    }
    return 'direct';
  };

  // Функция для извлечения Rutube ID
  const getRutubeId = (url) => {
    if (!url) return null;
    
    const patterns = [
      /(?:rutube\.ru\/video\/|rutube\.ru\/play\/embed\/)([a-zA-Z0-9]+)/,
      /rutube\.ru\/video\/([a-zA-Z0-9]+)\//
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };

  const toggleVideoPlayback = (itemId, videoUrl, videoType) => {
    if (videoType === 'rutube') {
      window.open(videoUrl, '_blank');
      return;
    }
    
    if (playingVideo === itemId) {
      const videoElement = videoRefs.current[itemId];
      if (videoElement) {
        videoElement.pause();
        videoElement.currentTime = 0;
      }
      setPlayingVideo(null);
    } else {
      if (playingVideo) {
        const currentVideoElement = videoRefs.current[playingVideo];
        if (currentVideoElement) {
          currentVideoElement.pause();
          currentVideoElement.currentTime = 0;
        }
      }
      
      setPlayingVideo(itemId);
      setTimeout(() => {
        const videoElement = videoRefs.current[itemId];
        if (videoElement && videoUrl) {
          videoElement.play().catch(e => console.log('Ошибка воспроизведения:', e));
        }
      }, 100);
    }
  };

const processItem = (item) => {
  // Получаем первое изображение из массива images
  const firstImage = item.images && item.images.length > 0 
    ? item.images[0] 
    : item.image || item.thumbnail || '/placeholder-image.jpg';

  return {
    ...item,
    features: item.features || [],
    colors: item.colors || ['#000000'],
    specifications: item.specifications || {
      threadCount: '',
      weight: '',
      material: '',
    },
    image: firstImage, // Используем первое изображение из массива
    thumbnail: firstImage, // Используем первое изображение как thumbnail
    videoUrl: item.videoUrl || null,
    videoDuration: item.videoDuration || null,
    videoType: getVideoType(item.videoUrl),
    categoryName: getCategoryName(item.category)
  };
};

  if (loading || categoriesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Загрузка каталога...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      <Header 
        onRefresh={fetchCatalogItems}
        title="ХОРЕКА"
        subtitle="Текстиль для ресторанов."
      />

      {error && (
        <div className="px-4 pt-4">
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Вкладки категорий */}
      <div className="px-4 pt-4">
        <div className="flex overflow-x-auto space-x-2 pb-4 scrollbar-hide">
          {categories.map(category => {
            const count = getCategoryCount(category.id);
            if (count === 0 && category.id !== 'all') return null;
            
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-amber-600 to-orange-500 text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span>{category.name}</span>
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                  selectedCategory === category.id
                    ? 'bg-white/20'
                    : 'bg-gray-700/50'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Каталог товаров */}
      <div className="px-4 pb-20">
        {catalogItems.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-300 mb-2">Каталог пуст</h3>
            <p className="text-gray-500 mb-6">Товары появятся здесь после их добавления через админ-панель</p>
            <button
              onClick={fetchCatalogItems}
              className="px-6 py-2 bg-gradient-to-r from-gray-800 to-gray-700 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Попробовать снова
            </button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-300 mb-2">В этой категории нет товаров</h3>
            <p className="text-gray-500 mb-6">Попробуйте выбрать другую категорию</p>
            <button
              onClick={() => setSelectedCategory('all')}
              className="px-6 py-2 bg-gradient-to-r from-gray-800 to-gray-700 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Показать все товары
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              {filteredItems.map(item => {
                const processedItem = processItem(item);
                const hasVideo = !!processedItem.videoUrl;
                const isPlaying = playingVideo === item.id;
                const videoType = processedItem.videoType;

                return (
                  <div 
                    key={item.id}
                    className="relative overflow-hidden rounded-2xl cursor-pointer group"
                    onClick={() => setSelectedItem(processedItem)}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-2xl"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent rounded-2xl"></div>
                    
                    <div className="relative z-10 p-4">
                      <div className="relative mb-3 overflow-hidden rounded-xl aspect-square">
                        <div className="relative w-full h-full">
                          <img 
                            src={processedItem.image} 
                            alt={processedItem.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '/placeholder-image.jpg';
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                          
                          {hasVideo && videoType === 'direct' && isPlaying && (
                            <div className="absolute inset-0 z-10">
                              <video
                                ref={el => videoRefs.current[item.id] = el}
                                src={processedItem.videoUrl}
                                className="w-full h-full object-cover"
                                playsInline
                                loop
                                muted
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleVideoPlayback(item.id, processedItem.videoUrl, processedItem.videoType);
                                }}
                              />
                              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleVideoPlayback(item.id, processedItem.videoUrl, processedItem.videoType);
                                  }}
                                  className="w-12 h-12 bg-black/60 rounded-full flex items-center justify-center hover:bg-black/80 transition-colors"
                                  title="Остановить видео"
                                >
                                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          )}
                          
                          {hasVideo && !isPlaying && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleVideoPlayback(item.id, processedItem.videoUrl, processedItem.videoType);
                              }}
                              className="absolute top-2 right-2 p-2 bg-black/70 rounded-full hover:bg-black/90 transition-colors z-20"
                              title={videoType === 'rutube' ? 'Открыть видео на Rutube' : 'Воспроизвести видео'}
                            >
                              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/>
                              </svg>
                            </button>
                          )}
                          
                          {hasVideo && !isPlaying && (
                            <div className="absolute top-2 left-2">
                              <div className={`px-2 py-1 rounded-lg text-xs font-bold ${
                                videoType === 'rutube' 
                                  ? 'bg-gradient-to-r from-purple-600 to-pink-600' 
                                  : 'bg-gradient-to-r from-blue-500 to-cyan-500'
                              }`}>
                                {videoType === 'rutube' ? 'Rutube' : 'Видео'}
                              </div>
                            </div>
                          )}
                          
                          {hasVideo && processedItem.videoDuration && (
                            <div className="absolute bottom-2 right-2">
                              <div className="px-2 py-1 bg-black/80 rounded-lg text-xs font-medium">
                                {processedItem.videoDuration}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <h3 className="font-bold text-sm mb-1 line-clamp-1">
                        {processedItem.name}
                      </h3>
                      <p className="text-xs text-gray-400 mb-3 line-clamp-2">
                        {processedItem.description}
                      </p>

                      {/* Категория товара */}
                      {processedItem.categoryName && processedItem.categoryName !== 'Все' && (
                        <div className="inline-block px-2 py-1 mb-3">
                          <span className="text-xs text-amber-400 font-medium">
                            {processedItem.categoryName}
                          </span>
                        </div>
                      )}

                      {/* Особенности */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        {(processedItem.features || []).slice(0, 2).map((feature, index) => (
                          <span key={index} className="px-2 py-1 bg-gray-800/50 text-xs rounded-lg">
                            {feature}
                          </span>
                        ))}
                      </div>

                      {/* Цвета и спецификации */}
                      {processedItem.colors && (
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-400">
                            {processedItem.specifications?.threadCount || ''}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 text-center">
              <div className="inline-flex items-center px-4 py-2 bg-gray-800/50 rounded-full">
                <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-400">
                  {selectedCategory === 'all' 
                    ? `Все товары: ${filteredItems.length}`
                    : `Категория "${getCategoryName(selectedCategory)}": ${filteredItems.length} товар(ов)`
                  }
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {selectedItem && (
        <ProductModal
          selectedItem={selectedItem}
          videoRefs={videoRefs}
          getRutubeId={getRutubeId}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
};

export default AlphaDogs;